/**
 * Solicitudes de demostración de los planes de contabilidad.
 *
 * PYME, PRO y PLUS no se compran desde la web: quien pulsa su tarjeta pide una demostración y
 * ventas le llama. Aquí viven las tres cosas que necesitan tanto el formulario público como la
 * bandeja de ventas: qué planes van por demo, quién puede leer las solicitudes y cómo se habla
 * con la base.
 *
 * Ojo con las dos listas de abajo: son copias de lo que dice la migración 035, que es la que
 * manda. Esta copia solo evita pintar un botón o un menú que la base rechazaría igualmente. Si se
 * cambia una, se cambia la otra.
 */

import { supabase } from './supabase';

/**
 * Los planes que se contratan hablando con ventas.
 *
 * En la base la lista está dentro de `solicitar_demo()`: mandar otro plan devuelve error, no una
 * solicitud silenciosa.
 */
export const PLANES_CON_DEMO = ['pyme', 'pro', 'plus'] as const;

export function planRequiereDemo(planId: string): boolean {
  return (PLANES_CON_DEMO as readonly string[]).includes(planId.toLowerCase());
}

/**
 * Las cuentas que ven la bandeja de solicitudes.
 *
 * No son «los administradores»: administrar cuentas y llevar la cartera comercial son dos cosas
 * distintas. El permiso de verdad lo aplica `public.puede_ver_solicitudes_demo()` en la base; esto
 * solo decide si se pinta el menú y la pantalla.
 */
export const EMAILS_SOLICITUDES_DEMO = ['carlosjuanhtc@contabird.com'];

export function gestionaSolicitudesDemo(email: string | null | undefined): boolean {
  return EMAILS_SOLICITUDES_DEMO.includes(String(email || '').trim().toLowerCase());
}

export type EstadoSolicitudDemo =
  | 'pendiente'
  | 'contactado'
  | 'agendada'
  | 'cerrada'
  | 'descartada';

/** Cómo se llama cada estado en pantalla y de qué color se pinta. */
export const ESTADOS_SOLICITUD: {
  id: EstadoSolicitudDemo;
  etiqueta: string;
  clase: string;
  icono: string;
}[] = [
  { id: 'pendiente', etiqueta: 'Pendiente', clase: 'bg-amber-100 text-amber-800 border-amber-200', icono: 'ri-time-line' },
  { id: 'contactado', etiqueta: 'Contactado', clase: 'bg-blue-100 text-blue-800 border-blue-200', icono: 'ri-phone-line' },
  { id: 'agendada', etiqueta: 'Demo agendada', clase: 'bg-indigo-100 text-indigo-800 border-indigo-200', icono: 'ri-calendar-check-line' },
  { id: 'cerrada', etiqueta: 'Cerrada (cliente)', clase: 'bg-green-100 text-green-800 border-green-200', icono: 'ri-check-double-line' },
  { id: 'descartada', etiqueta: 'Descartada', clase: 'bg-gray-100 text-gray-600 border-gray-200', icono: 'ri-close-circle-line' },
];

export interface SolicitudDemo {
  id: string;
  plan_id: string;
  plan_nombre: string;
  nombre: string;
  email: string;
  telefono: string;
  empresa: string | null;
  rnc: string | null;
  fecha_preferida: string | null;
  mensaje: string | null;
  origen: string | null;
  estado: EstadoSolicitudDemo;
  notas: string | null;
  atendida_por: string | null;
  atendida_en: string | null;
  created_at: string;
  updated_at: string;
}

/** Lo que manda el formulario público. */
export interface DatosSolicitudDemo {
  planId: string;
  nombre: string;
  email: string;
  telefono: string;
  empresa?: string;
  rnc?: string;
  fechaPreferida?: string;
  mensaje?: string;
  /** Desde qué pantalla se envió: 'home' o 'planes'. */
  origen?: string;
}

/**
 * Envía una solicitud. Va por RPC y no por un INSERT porque quien la manda no tiene sesión: la
 * tabla no acepta escrituras de nadie, solo esta función (ver migración 035).
 *
 * `duplicada` es cierto cuando el mismo correo ya había pedido ese plan hace un momento. Se
 * devuelve para que la pantalla dé las gracias igual —al visitante no le importa— sin que ventas
 * acabe con la ficha repetida.
 */
export async function enviarSolicitudDemo(
  datos: DatosSolicitudDemo,
): Promise<{ id: string; duplicada: boolean }> {
  const { data, error } = await supabase.rpc('solicitar_demo', {
    p_plan_id: datos.planId,
    p_nombre: datos.nombre,
    p_email: datos.email,
    p_telefono: datos.telefono,
    p_empresa: datos.empresa || null,
    p_rnc: datos.rnc || null,
    p_fecha_preferida: datos.fechaPreferida || null,
    p_mensaje: datos.mensaje || null,
    p_origen: datos.origen || null,
  });

  if (error) throw new Error(error.message || 'No se pudo enviar la solicitud.');

  const respuesta = (data ?? {}) as { id?: string; duplicada?: boolean };
  return { id: String(respuesta.id ?? ''), duplicada: !!respuesta.duplicada };
}

/** Todas las solicitudes, la más reciente primero. La RLS ya filtra quién puede leerlas. */
export async function listarSolicitudesDemo(): Promise<SolicitudDemo[]> {
  const { data, error } = await supabase
    .from('solicitudes_demo')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message || 'No se pudieron cargar las solicitudes.');
  return (data as SolicitudDemo[]) || [];
}

/**
 * Guarda el seguimiento de una solicitud: en qué estado está y lo que apuntó ventas.
 *
 * `atendida_en` se sella la primera vez que deja de estar pendiente, que es cuando alguien la
 * tocó de verdad. `updated_at` se pone a mano porque la tabla no tiene disparador.
 */
export async function actualizarSolicitudDemo(
  id: string,
  cambios: { estado?: EstadoSolicitudDemo; notas?: string | null },
  usuarioId?: string | null,
): Promise<void> {
  const parche: Record<string, unknown> = { ...cambios, updated_at: new Date().toISOString() };

  if (cambios.estado && cambios.estado !== 'pendiente') {
    parche.atendida_en = new Date().toISOString();
    if (usuarioId) parche.atendida_por = usuarioId;
  }

  const { error } = await supabase.from('solicitudes_demo').update(parche).eq('id', id);
  if (error) throw new Error(error.message || 'No se pudo guardar el cambio.');
}

export async function borrarSolicitudDemo(id: string): Promise<void> {
  const { error } = await supabase.from('solicitudes_demo').delete().eq('id', id);
  if (error) throw new Error(error.message || 'No se pudo borrar la solicitud.');
}
